import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { fecha } from "../lib/formato";
import { Aviso, Cargando, Modal, Panel } from "./piezas";
import { useSesion } from "../lib/sesion";
import { useIdioma } from "../lib/idioma";

/**
 * El contrato de un socio, en su ficha.
 *
 * Enseña **lo que firmó**, no el texto de hoy: si el club cambió el contrato
 * después, el suyo sigue siendo el suyo. Y se imprime tal cual, con sus datos
 * ya puestos, para la carpeta de papel que casi todas las asociaciones tienen
 * que seguir teniendo.
 */

interface DelSocio {
  firmado: boolean;
  aceptadoEn: string | null;
  numero: number;
  texto: string;
  firmaUrl: string | null;
  socio: { nombre: string; apellidos: string; numeroSocio: number };
}

export default function ContratoDelSocio({ socioId }: { socioId: string }) {
  const { t } = useIdioma();
  const { puede } = useSesion();
  const clienteConsultas = useQueryClient();
  const [abierto, setAbierto] = useState(false);
  const [error, setError] = useState("");
  const [firmando, setFirmando] = useState(false);

  const contrato = useQuery({
    queryKey: ["contrato-socio", socioId],
    queryFn: () => api<DelSocio>(`/contrato/socio/${socioId}`),
    // Un club que no usa contrato no tiene que ver un error rojo en la ficha.
    retry: false,
  });

  // Sin contrato escrito el bloque entero sobra: no hay nada que enseñar.
  if (contrato.isError) return null;

  async function firmar() {
    setError("");
    setFirmando(true);
    try {
      await api(`/contrato/socio/${socioId}/firmar`, { metodo: "POST" });
      void clienteConsultas.invalidateQueries({ queryKey: ["contrato-socio", socioId] });
      void clienteConsultas.invalidateQueries({ queryKey: ["contrato-pendientes"] });
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : t("cs.fallo"));
    } finally {
      setFirmando(false);
    }
  }

  const datos = contrato.data;

  return (
    <>
      <Panel titulo={t("cs.titulo")}>
        <Aviso tipo="error">{error}</Aviso>

        {contrato.isLoading || !datos ? (
          <Cargando />
        ) : (
          <>
            <p style={{ marginTop: 0 }}>
              {datos.firmado ? (
                datos.aceptadoEn ? (
                  t("cs.firmoEl", { numero: datos.numero, fecha: fecha(datos.aceptadoEn) })
                ) : (
                  t("cs.firmo", { numero: datos.numero })
                )
              ) : (
                <span className="suave">{t("cs.sinFirmar", { numero: datos.numero })}</span>
              )}
            </p>

            <div className="fila">
              <button className="boton secundario" onClick={() => setAbierto(true)}>
                {t("cs.verEImprimir")}
              </button>
              {!datos.firmado && puede("editarSocio") && (
                <button className="boton" disabled={firmando} onClick={firmar}>
                  {firmando ? t("cs.apuntando") : t("cs.marcarFirmado")}
                </button>
              )}
            </div>

            {!datos.firmado && (
              <p className="suave pequeno" style={{ marginBottom: 0, marginTop: 12 }}>
                {t("cs.tambienPortal")} <Link to="/socios/contrato">{t("cs.verContratoClub")}</Link>
              </p>
            )}
          </>
        )}
      </Panel>

      {abierto && datos && (
        <Modal
          titulo={t("cs.modalTitulo", { numero: datos.socio.numeroSocio })}
          alCerrar={() => setAbierto(false)}
          acciones={
            <>
              <button className="boton secundario" onClick={() => setAbierto(false)}>
                {t("g.cerrar")}
              </button>
              <button className="boton" onClick={() => window.print()}>
                {t("d.imprimir")}
              </button>
            </>
          }
        >
          {!datos.firmado && (
            <p className="sin-imprimir">
              <Aviso tipo="atencion">
                {t("cs.noFirmadoAun")}
              </Aviso>
            </p>
          )}
          <div className="documento">
            <pre className="texto-contrato">{datos.texto}</pre>

            {/* Si firmó en la tablet, su firma va impresa donde iría la de
                puño y letra. Dejar la raya en blanco haría pensar que falta. */}
            <div className="firma-contrato">
              <span>
                {datos.firmaUrl && (
                  <img className="firma-imagen" src={datos.firmaUrl} alt={t("cs.firmaDelSocio")} />
                )}
                {t("cs.firmaDelSocio")}
              </span>
              {!datos.firmado && <span>{t("cs.firmaYSello")}</span>}
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
